import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/config/constants';
import { getHomePathForUser } from '@/utils/roleHome';
import PageLoading from './components/common/PageLoading';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';
import ProtectedRoute, { RoleRoute, MenuAccessRoute } from './components/common/ProtectedRoute';
import AgentBotGate from './components/common/AgentBotGate';
import AgentLegacySelectBotRedirect from './components/agent/AgentLegacySelectBotRedirect';

// Auth Pages
const Login = lazy(() => import('./pages/auth/Login'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ChangePassword = lazy(() => import('./pages/auth/ChangePassword'));

// Bot Pages
const BotList = lazy(() => import('./pages/bots/BotList'));
const BotEditor = lazy(() => import('./components/bot-editor/BotEditor'));
const BotConfig = lazy(() => import('./pages/bots/BotConfig'));
const GoogleConnectionsPage = lazy(() => import('./pages/integrations/GoogleConnectionsPage'));
const GoogleConnectPage = lazy(() => import('./pages/integrations/GoogleConnectPage'));
const AiBotBuilder = lazy(() => import('./pages/ai-builder/AiBotBuilder'));

// Channel Pages
const ChannelList = lazy(() => import('./pages/channels/ChannelList'));
const ChannelDetail = lazy(() => import('./pages/channels/ChannelDetail'));

// Action Pages
const ActionList = lazy(() => import('./pages/actions/ActionList'));
const ActionEditor = lazy(() => import('./pages/actions/ActionEditor'));

// Chat log + Live Chat pages
const LiveChat = lazy(() => import('./pages/livechat/LiveChat'));
const AgentWorkspace = lazy(() => import('./pages/livechat/AgentWorkspace'));
// Log Pages
const LogViewer = lazy(() => import('./pages/logs/LogViewer'));

// Analytics Pages
const PlatformAnalytics = lazy(() => import('./pages/analytics/PlatformAnalytics'));
const BotAnalytics = lazy(() => import('./pages/analytics/BotAnalytics'));
const TenantAnalyticsPage = lazy(() => import('./pages/analytics/TenantAnalyticsPage'));
const MonitoringPage = lazy(() => import('./pages/monitoring/MonitoringPage'));

// Dashboard (platform; tenant home is tenant hub)
const DashboardRoute = lazy(() => import('./pages/dashboard/DashboardRoute'));

// Tenant reporting
const TenantReporting = lazy(() => import('./pages/reporting/TenantReporting'));

// Agent Analytics
const AgentAnalytics = lazy(() => import('./pages/agent-analytics/AgentAnalytics'));

// Tenant Management
const TenantList = lazy(() => import('./pages/tenants/TenantList'));
const TenantHubRoute = lazy(() => import('./pages/tenants/TenantHubRoute'));
const TenantWorkspaceSettingsRoute = lazy(() => import('./pages/tenants/TenantWorkspaceSettingsRoute'));
const TenantAiSettingsRoute = lazy(() => import('./pages/tenants/TenantAiSettingsRoute'));
const TenantBots = lazy(() => import('./pages/tenants/TenantBots'));
const BotOverview = lazy(() => import('./pages/bots/BotOverview'));

// User Management
const UserList = lazy(() => import('./pages/users/UserList'));

// Tickets
const TicketList = lazy(() => import('./pages/tickets/TicketList'));
const TicketDetail = lazy(() => import('./pages/tickets/TicketDetail'));

// Admin Pages
const AiChats = lazy(() => import('./pages/admin/AiChats'));
const AiLearning = lazy(() => import('./pages/admin/AiLearning'));
const WorkspaceSettings = lazy(() => import('./pages/admin/WorkspaceSettings'));
const TenantAiSettings = lazy(() => import('./pages/admin/TenantAiSettings'));
const AdminWorkspaceSettingsRedirect = lazy(() => import('./pages/admin/AdminWorkspaceSettingsRedirect'));

// Skills Management
const SkillList = lazy(() => import('./pages/skills/SkillList'));

// Knowledge Base
const KnowledgeBaseList = lazy(() => import('./pages/knowledgebase/KnowledgeBaseList'));

// Help
const HelpCenter = lazy(() => import('./pages/help/HelpCenter'));

// Profile & platform config
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const ConfigurationPage = lazy(() => import('./pages/config/ConfigurationPage'));

// Announcements
const AnnouncementList = lazy(() => import('./pages/announcements/AnnouncementList'));

// Internal Chat Logs
const InternalChatLogs = lazy(() => import('./pages/chat-logs/InternalChatLogs'));

// Developer Docs
const DevDocs = lazy(() => import('./pages/developer-docs/DevDocs'));

// Error Pages
const NotFound = lazy(() => import('./pages/NotFound'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));


const RoleBasedRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={getHomePathForUser(user)} replace />;
};

/** Avoids sending agents to /dashboard (they lack access) and handles must-change-password. */
const LoginRouteGate = () => {
  const { isAuthenticated, mustChangePassword, user } = useAuth();
  if (!isAuthenticated) return <Login />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  return <Navigate to={getHomePathForUser(user)} replace />;
};

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoading />}>
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path={ROUTES.LOGIN} element={<LoginRouteGate />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
      </Route>

      {/* A client connects their Google account from a link: no Studio login */}
      <Route path="/connect/google/:token" element={<GoogleConnectPage />} />

      {/* Change Password - Protected but without main layout */}
      <Route element={<ProtectedRoute />}>
        <Route path="/change-password" element={<ChangePassword />} />
      </Route>

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<MainLayout />}>
          <Route path="/" element={<RoleBasedRedirect />} />
          
          <Route path="/dashboard" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator', 'agent']}>
              <MenuAccessRoute menuKey="/dashboard"><DashboardRoute /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/agent/select-bot" element={
            <RoleRoute allowedRoles={['agent']}>
              <AgentLegacySelectBotRedirect />
            </RoleRoute>
          } />

          <Route path="/dashboard/workspace-settings" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/dashboard/workspace-settings"><WorkspaceSettings /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/dashboard/ai-settings" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/dashboard/ai-settings"><TenantAiSettings /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/reporting" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/reporting"><TenantReporting /></MenuAccessRoute>
            </RoleRoute>
          } />
          
          {/* Bot Routes */}
          <Route path="/bots/:botId/overview" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <BotOverview />
            </RoleRoute>
          } />
          <Route path="/bots" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <MenuAccessRoute menuKey="/bots"><BotList /></MenuAccessRoute>
            </RoleRoute>
          } />
          
          {/* Channel Routes - Superadmin only */}
          <Route path="/channels" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <MenuAccessRoute menuKey="/channels"><ChannelList /></MenuAccessRoute>
            </RoleRoute>
          } />
          <Route path="/channels/:id" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <ChannelDetail />
            </RoleRoute>
          } />
          
          {/* `/tenants/:id` — superadmin hub; tenant admin/creator → redirect to `/dashboard` */}
          <Route path="/tenants/:tenantId/users" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <UserList />
            </RoleRoute>
          } />
          <Route path="/tenants/:tenantId/bots" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <TenantBots />
            </RoleRoute>
          } />
          <Route path="/tenants/:tenantId/workspace-settings" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <TenantWorkspaceSettingsRoute />
            </RoleRoute>
          } />
          <Route path="/tenants/:tenantId/ai-settings" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <TenantAiSettingsRoute />
            </RoleRoute>
          } />
          <Route path="/tenants/:tenantId/reporting" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/reporting"><TenantReporting /></MenuAccessRoute>
            </RoleRoute>
          } />
          <Route path="/tenants/:tenantId/agent-analytics" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/agent-analytics"><AgentAnalytics /></MenuAccessRoute>
            </RoleRoute>
          } />
          <Route path="/tenants/:tenantId/tickets/:id" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator', 'agent']}>
              <TicketDetail />
            </RoleRoute>
          } />
          <Route path="/tenants/:tenantId/tickets" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator', 'agent']}>
              <MenuAccessRoute menuKey="/tickets"><TicketList /></MenuAccessRoute>
            </RoleRoute>
          } />
          <Route path="/tenants/:tenantId" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <TenantHubRoute />
            </RoleRoute>
          } />
          <Route path="/tenants" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <MenuAccessRoute menuKey="/tenants"><TenantList /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/platform/users" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <MenuAccessRoute menuKey="/platform/users"><UserList /></MenuAccessRoute>
            </RoleRoute>
          } />
          
          {/* User Routes - tenant admins; superadmin /users redirects to /platform/users */}
          <Route path="/users" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/users"><UserList /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/tickets" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator', 'agent']}>
              <MenuAccessRoute menuKey="/tickets"><TicketList /></MenuAccessRoute>
            </RoleRoute>
          } />
          <Route path="/tickets/:id" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator', 'agent']}>
              <TicketDetail />
            </RoleRoute>
          } />
          
          {/* Skills Routes - Superadmin only */}
          <Route path="/skills" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <MenuAccessRoute menuKey="/skills"><SkillList /></MenuAccessRoute>
            </RoleRoute>
          } />
          
          {/* Action Routes */}
          <Route path="/integrations/google" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <MenuAccessRoute menuKey="/integrations/google"><GoogleConnectionsPage /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/actions" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <MenuAccessRoute menuKey="/actions"><ActionList /></MenuAccessRoute>
            </RoleRoute>
          } />
          
          {/* Knowledge Base Route */}
          <Route path="/knowledge-base" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <MenuAccessRoute menuKey="/knowledge-base"><KnowledgeBaseList /></MenuAccessRoute>
            </RoleRoute>
          } />
          
          {/* Chat log — agents need a workspace bot (AgentBotGate) */}
          <Route path="/livechat" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'agent']}>
              <AgentBotGate>
                <MenuAccessRoute menuKey="/livechat"><LiveChat /></MenuAccessRoute>
              </AgentBotGate>
            </RoleRoute>
          } />
          
          {/* Live Chat (queue) — agents need a workspace bot (AgentBotGate) */}
          <Route path="/livechat/workspace" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'agent']}>
              <AgentBotGate>
                <MenuAccessRoute menuKey="/livechat/workspace"><AgentWorkspace /></MenuAccessRoute>
              </AgentBotGate>
            </RoleRoute>
          } />
          
          {/* Logs Route - Admin+ */}
          <Route path="/logs" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/logs"><LogViewer /></MenuAccessRoute>
            </RoleRoute>
          } />
          
          {/* Platform / tenant analytics — superadmin sees all tenants; admin & creator see their tenant */}
          <Route path="/analytics" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <MenuAccessRoute menuKey="/analytics"><PlatformAnalytics /></MenuAccessRoute>
            </RoleRoute>
          } />
          <Route path="/analytics/tenant/:tenantId" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <MenuAccessRoute menuKey="/analytics"><TenantAnalyticsPage /></MenuAccessRoute>
            </RoleRoute>
          } />
          {/* Bot / workspace analytics (tenant or bot scope via query) */}
          <Route path="/analytics/bot" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <BotAnalytics />
            </RoleRoute>
          } />

          <Route path="/monitoring" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <MenuAccessRoute menuKey="/monitoring"><MonitoringPage /></MenuAccessRoute>
            </RoleRoute>
          } />

          {/* Agent Analytics */}
          <Route path="/agent-analytics" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/agent-analytics"><AgentAnalytics /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/help" element={
            <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
              <MenuAccessRoute menuKey="/help"><HelpCenter mode="admin" /></MenuAccessRoute>
            </RoleRoute>
          } />
          <Route path="/help/agent" element={
            <RoleRoute allowedRoles={['agent']}>
              <MenuAccessRoute menuKey="/help/agent"><HelpCenter mode="agent" /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/profile" element={<ProfilePage />} />

          <Route path="/config" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <MenuAccessRoute menuKey="/config"><ConfigurationPage /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/announcements" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/announcements"><AnnouncementList /></MenuAccessRoute>
            </RoleRoute>
          } />

          <Route path="/internal-chat-logs" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/internal-chat-logs"><InternalChatLogs /></MenuAccessRoute>
            </RoleRoute>
          } />
          
          {/* Admin: AI Chats */}
          <Route path="/admin/ai-chats" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <MenuAccessRoute menuKey="/admin/ai-chats"><AiChats /></MenuAccessRoute>
            </RoleRoute>
          } />
          
          {/* Admin: AI Learning Analytics */}
          <Route path="/admin/ai-learning" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <MenuAccessRoute menuKey="/admin/ai-learning"><AiLearning /></MenuAccessRoute>
            </RoleRoute>
          } />

          {/* Legacy /admin/workspace-settings → hub-scoped Live Chat Settings */}
          <Route path="/admin/workspace-settings" element={
            <RoleRoute allowedRoles={['superadmin', 'admin']}>
              <AdminWorkspaceSettingsRedirect />
            </RoleRoute>
          } />
          
          {/* Developer Docs - Superadmin only */}
          <Route path="/developer-docs" element={
            <RoleRoute allowedRoles={['superadmin']}>
              <MenuAccessRoute menuKey="/developer-docs"><DevDocs /></MenuAccessRoute>
            </RoleRoute>
          } />

          {/* More routes will be added here */}
        </Route>
        
        {/* AI Bot Builder - Full screen without main layout */}
        <Route path="/bots/new/ai" element={
          <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
            <AiBotBuilder />
          </RoleRoute>
        } />
        
        {/* Bot Editor - Full screen without main layout */}
        <Route path="/bots/:id/editor" element={
          <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
            <BotEditor />
          </RoleRoute>
        } />
        
        {/* Bot Config - Full screen */}
        <Route path="/bots/:id/config" element={
          <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
            <BotConfig />
          </RoleRoute>
        } />
        
        {/* Action Editor - Full screen */}
        <Route path="/actions/:id/edit" element={
          <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
            <ActionEditor />
          </RoleRoute>
        } />
        <Route path="/actions/new" element={
          <RoleRoute allowedRoles={['superadmin', 'admin', 'creator']}>
            <ActionEditor />
          </RoleRoute>
        } />
      </Route>

      {/* Error Pages */}
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

export default AppRoutes;

